import React, { useRef, useState } from 'react'
import styled from 'styled-components'

import Modal from 'components/Modal'
import CollapsiblePanel from 'components/CollapsiblePanel'

import { removeScriptTags } from 'helpers/formatters'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { SiteMessageDetail } from 'types/SiteMessageDetail'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

type props = {
  siteMessage: SiteMessageDetail
  className?: string,
  showCollapsed?: boolean | false,
  onImageClickCallback?: () => void
  onActionButtonClickCallback?: () => void
  onTextClickCallback?: () => void
}

const Body = ({
  siteMessage,
  onImageClickCallback,
  onActionButtonClickCallback,
  onTextClickCallback,
  className,
  showCollapsed = false
}: props) => {
  const [showModal, setShowModal] = useState(false)

  const headerRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)

  const handleImageClick = (event: React.MouseEvent) => {
    event.preventDefault()

    if (showCollapsed && (!open || siteMessage.imageType === 'icon')) {
      headerRef.current?.click()

      return
    }

    if (siteMessage.imageType === 'image') {
      setShowModal(true)
      onImageClickCallback && onImageClickCallback()
    }
  }

  const TextWithActionButton = (
    <>
      <S.Message
        onClick={() => { if (onTextClickCallback) onTextClickCallback() }}
        dangerouslySetInnerHTML={{ __html: removeScriptTags(siteMessage.message) }}
      />

      {siteMessage.actionButtonCaption && (
        <S.ActionButton
          tabIndex={-1}
          href={siteMessage.actionButtonLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button decoration="secondary" text={siteMessage.actionButtonCaption} onPress={() => { if (onActionButtonClickCallback) onActionButtonClickCallback() }} />
        </S.ActionButton>
      )}
    </>
  )

  const Image = (
    <S.ImageContainer showCollapsed={showCollapsed}
    onClick={(e) => handleImageClick(e)}
    open={open}
    isTypeImage={siteMessage.imageType === 'image'}
    >
      <S.Image src={siteMessage.image} alt="" open={open} role="img" icon={siteMessage.imageType === 'icon'} />
      {(siteMessage.imageType === 'image' && ((showCollapsed && open) || (!showCollapsed))) && <S.ClickToExpand>click to expand</S.ClickToExpand>}
    </S.ImageContainer>
  )

  return (
    <>
      <S.Container className={className} imageType={siteMessage.imageType} open={open}>
        {showCollapsed
          ? (
            <S.CollapsiblePanel caretHorizontalPosition="left" caretVerticalPosition="start" onClickCallback={() => setOpen(!open)} headerComponent={
              <S.ImageAndTextContainer>
                {siteMessage.image && Image}
                <S.TitleAndMessageContainer>
                  <S.Title ref={headerRef}>{siteMessage.title}</S.Title>
                  {open && (
                    <S.MessageText showCollapsed={showCollapsed}>{TextWithActionButton}</S.MessageText>
                  )}
                </S.TitleAndMessageContainer>
              </S.ImageAndTextContainer>
            }
            />
          )
          : (
            <S.ImageAndTextContainer>
              {siteMessage.image && Image}
              <S.TitleAndMessageContainer>
                <S.Title>{siteMessage.title}</S.Title>
                <S.MessageText>{TextWithActionButton}</S.MessageText>
              </S.TitleAndMessageContainer>
            </S.ImageAndTextContainer>
          )
        }

      </S.Container>
      <S.Modal
        variant="lg"
        messageType={siteMessage.messageType}
        title={siteMessage.title}
        isDisplayed={showModal}
        closeCallback={() => setShowModal(false)}
      >
        {siteMessage.image && <S.ModalImage src={siteMessage.image} alt="" /> /* alt intentionally blank as the src is dynamic */}

        <S.ModalMessageText>
          {TextWithActionButton}
        </S.ModalMessageText>
      </S.Modal>
    </>
  )
}

export default Body

const largeImageContainerWidth = '350px'
const mediumImageContainerWidth = '200px'
const iconContainerWidth = '64px'

const S = {
  Container: styled.div.withConfig({
    shouldForwardProp: (prop) => !['imageType', 'open'].includes(prop)
  })<{ imageType: string, open: boolean }>`
    display: flex;
    flex-direction: row;
    overflow: hidden;

    @media ${screenSize.small} {
      display: ${({ imageType, open }) => imageType === 'image' && open ? 'block' : 'flex'};
    }
  `,
  ImageContainer: styled.div.withConfig({
    shouldForwardProp: (prop) => !['showCollapsed', 'isTypeImage', 'open'].includes(prop)
  })<{ isTypeImage: boolean, open: boolean, showCollapsed?: boolean | null }>`
    width: ${({ showCollapsed, isTypeImage, open }) => showCollapsed
    ? isTypeImage && open ? largeImageContainerWidth : iconContainerWidth
    : isTypeImage ? largeImageContainerWidth : iconContainerWidth};
    min-width: 40px;
    margin-right: 10px;
    transition: width 0.5s;
    cursor: pointer;
    ${({ open }) => !open && 'max-height: 30px;'}

    @media ${screenSize.medium} {
      width: ${({ showCollapsed, isTypeImage, open }) => showCollapsed
    ? isTypeImage && open ? mediumImageContainerWidth : iconContainerWidth
    : isTypeImage ? mediumImageContainerWidth : iconContainerWidth};
    }

    @media ${screenSize.small} {
      width: ${({ showCollapsed, isTypeImage, open }) => showCollapsed
    ? isTypeImage && open ? '100%' : iconContainerWidth
    : isTypeImage ? '100%' : iconContainerWidth};
    }
  `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['open', 'icon'].includes(prop)
  })<{ open: boolean, icon: boolean }>`
    display: block;
    margin: 0 auto;
    width: 100%;
    ${({ open, icon }) => !open && icon && 'height: 30px; width: 30px;'}
  `,
  ImageAndTextContainer: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
  `,
  ClickToExpand: styled.div`
    text-align: center;
    background: ${color('imageCaptionBackground')};
    color: ${color('imageCaptionText')};
    font-size: .8em;
    padding: 3px;
    text-transform: uppercase;
    text-wrap: nowrap;
  `,
  CollapsiblePanel: styled(CollapsiblePanel)`
    width: 100%;
    margin-left: 10px;
  `,
  MessageText: styled.div.withConfig({
    shouldForwardProp: (prop) => !['showCollapsed'].includes(prop)
  })<{ showCollapsed?: boolean | null }>`
    display: inline-block;
    width: 100%;
    word-break: break-word;
    padding-right: 10px;
    padding-bottom: 10px;
  `,
  Title: styled.div`
    width: 100%;
    text-transform: uppercase;
    font-weight: bold;
    font-size: 1.4em;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  Message: styled.div``,
  TitleAndMessageContainer: styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  ActionButton: styled.a`
    margin: 10px 0 auto auto;
    width: fit-content;
    display: block;
  `,
  ModalImage: styled.img`
    display: block;
    margin: 0 auto;
    border: 1px solid ${color('border')};
    max-width: 100%;
  `,
  ModalMessageText: styled.div`
    display: inline-block;
    margin-top: 20px;
    font-size: 1.2em;
  `,
  Modal: styled(Modal).withConfig({
    shouldForwardProp: (prop) => !['messageType'].includes(prop)
  })<{ messageType: string }>`
    background-color: ${({ messageType }) => messageType === 'info' ? color('siteMessageInfoBackground') : color('siteMessageWarningBackground')};

    @media ${screenSize.small} {
      min-height: 100%;
    }
  `
}
